import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, FileVideo, HardDriveUpload, Info } from "lucide-react";

export default function ImportPage() {
  const navigate = useNavigate();
  const inputRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [uploading, setUploading] = useState(false);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please select a video file"); return; }
    setFile(f);
    setName(f.name.replace(/\.[^/.]+$/, ""));
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "source");
      const asset = await api.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const proj = await api.post("/projects", { name: name || file.name, source_asset_id: asset.data.id });
      toast.success("Imported! Opening editor…");
      navigate(`/editor/${proj.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen p-10">
      <div className="rf-fade-up mx-auto max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF]">Step 1</p>
        <h1 className="mt-1 font-heading text-4xl font-bold">Import a Reel</h1>
        <p className="mt-2 text-gray-400">Upload your licensed AI reel to start branding it.</p>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`mt-8 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed py-16 transition-colors ${
            dragOver ? "border-[#00E5FF] bg-[#00E5FF]/5" : "border-white/15 bg-[#0f0f0f] hover:border-white/30"
          }`}
        >
          <input ref={inputRef} type="file" accept="video/*" hidden data-testid="file-input" onChange={(e) => pick(e.target.files?.[0])} />
          {file ? (
            <div className="flex flex-col items-center gap-2 text-center">
              <FileVideo className="h-12 w-12 text-[#00E5FF]" />
              <span className="font-medium">{file.name}</span>
              <span className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 text-center">
              <UploadCloud className="h-12 w-12 text-gray-500" />
              <span className="font-medium">Drag & drop your video here</span>
              <span className="text-sm text-gray-500">or click to browse · MP4, MOV, WEBM</span>
            </div>
          )}
        </div>

        {file && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-300">Project name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="project-name-input"
                className="h-11 w-full rounded-md border border-white/10 bg-[#1A1A1A] px-3 text-sm text-white focus:border-[#00E5FF] focus:outline-none focus:ring-1 focus:ring-[#00E5FF]"
              />
            </div>
            <button
              onClick={submit}
              disabled={uploading}
              data-testid="upload-submit-btn"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#00E5FF] font-semibold text-black transition-colors hover:bg-[#33EBFF] disabled:opacity-60"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardDriveUpload className="h-4 w-4" />}
              {uploading ? "Uploading…" : "Import & Open Editor"}
            </button>
          </div>
        )}

        <div className="mt-8 flex gap-2 rounded-md border border-white/10 bg-[#0f0f0f] p-4 text-sm text-gray-400">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" />
          <span>
            Google Drive import is on the roadmap. For now, upload directly — files are stored securely in object storage and can be exported back to Drive later.
          </span>
        </div>
      </div>
    </div>
  );
}
